function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useEffect, useRef } from 'react';
import { getScrollParent, createLink, jsonFilter, checkIfStuck, number } from '../../utils';
import { NavLink } from 'react-router-dom';
import { parse, stringify } from 'query-string';
import { useGlobalValue } from '../../state/GlobalProvider';
import ListHeader from './ListHeader';
import ListFooter from './ListFooter';
import styles from './List.module.sass';
import RegularButton from "../inputs/RegularButton/RegularButton";

const checkVisibleItems = (elem, numberOfItems, cb) => {
  if (elem && elem.children.length && typeof document !== 'undefined') {
    const w = elem.ownerDocument.defaultView || elem.ownerDocument.parentView || window;
    const rect = elem.getBoundingClientRect();
    const {
      innerHeight
    } = w;
    const childStyle = w.getComputedStyle(elem.children[0]);
    const itemsHeight = parseFloat(childStyle.height) + parseFloat(childStyle.marginBottom);
    const visibleHeight = Math.max(numberOfItems * itemsHeight, window.innerHeight - rect.top);
    const availableHeight = Math.max(0, Math.min(visibleHeight, innerHeight));
    const visibleRows = Math.min(numberOfItems, Math.ceil(availableHeight / itemsHeight) + 2) || 0;
    const containerHeight = itemsHeight * numberOfItems;
    const firstItem = Math.min(numberOfItems - 1, Math.max(0, Math.floor(Math.max(0, -rect.top) / itemsHeight) - 1));
    const padding = itemsHeight * firstItem;
    cb([firstItem, visibleRows, containerHeight, padding]);
  } else if (elem && numberOfItems > 0 && typeof document !== 'undefined') {
    cb([0, 1, 100, 0]);
  }
};

const checkEnding = elem => {
  if (!elem) {
    return false;
  }

  const w = elem.ownerDocument.defaultView || elem.ownerDocument.parentView || window;
  const rect = elem.getBoundingClientRect();
  const {
    innerHeight
  } = w;
  const heightPercentage = Math.round(innerHeight / rect.bottom * 100) / 100;
  return heightPercentage > 0.75;
};

function getCard(card, cardKey) {
  if (card && typeof card !== 'object') {
    return card;
  } else if (card && cardKey) {
    return card[cardKey];
  }

  return null;
}

function getHeader(c, othersProps) {
  if (c && typeof c === 'object') {
    return /*#__PURE__*/React.createElement(c.component, _extends({}, c.props, othersProps));
  }

  return null;
}

function getHeaderMap(component, othersProps) {
  return (component || []).map((c, index) => {
    if (c && typeof c === 'object') {
      return /*#__PURE__*/React.createElement(c.component, _extends({
        key: index
      }, c.props, othersProps));
    }

    return null;
  });
}

const List = props => {
  const {
    id,
    basePath,
    items: childList,
    filter,
    sort,
    headerComponent,
    headerListComponent,
    headerListButtonsActions,
    card,
    header,
    cardKey,
    className,
    style,
    modifiers,
    wrapper,
    activity,
    footer,
    handleNoResults = true,
    onClick,
    metadata,
    status,
    page,
    pageSize,
    checkbox,
    totalizers
  } = props;
  const filtersObj = jsonFilter(filter);
  const {
    i18n,
    params,
    location,
    history,
    proxy,
    reload,
    user,
    pushModal
  } = useGlobalValue();
  useEffect(() => {
    setCheckList(prevState => {
      const list = (childList || []).map(item => {
        const prevStateItem = (prevState || []).find(i => i.id === item.id);
        return Object.assign({}, item, {
          checked: prevStateItem ? prevStateItem.checked : false
        });
      });
      return list;
    });
  }, [childList]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [checkList, setCheckList] = useState([]);

  const onCheckBoxChange = (checkName, isChecked) => {
    let isAllChecked = checkName === 'all' && isChecked;
    let isAllUnChecked = checkName === 'all' && !isChecked;
    const checked = isChecked;
    const list = checkList.map((item, index) => {
      if (isAllChecked || item.id === checkName) {
        return Object.assign({}, item, {
          checked
        });
      } else if (isAllUnChecked) {
        return Object.assign({}, item, {
          checked: false
        });
      }

      return item;
    });
    let allSelected = list.findIndex(item => item.checked === false) === -1 || isAllChecked;
    setIsAllSelected(allSelected);
    setCheckList(list);
  };

  const modifiersClasses = (modifiers || []).map(m => styles[m]).filter(m => m).join(' ');
  const [visibleItems, setVisibleItems] = useState([0, 1, 10, 0]);
  const [footerStuck, setFooterStuck] = useState(false);
  const listElem = useRef(null);
  const footerElem = useRef(null);
  const [first, numberOfVisible, containerHeight, paddingTop] = visibleItems;
  const currentPage = page ? parseInt(page, 10) : 0;
  const currentPageSize = pageSize ? parseInt(pageSize, 10) : 24;
  const totalItems = (currentPage + 1) * currentPageSize;
  const hasMoreItems = metadata && metadata.hasMore && metadata.total && totalItems < metadata.total;
  const loadMore = hasMoreItems ? () => {
    const queryParams = parse(location.search);
    const prevPage = parseInt(queryParams.page || 0, 10);
    const fetchingPage = ((status || {}).pagging || [])[2] || 0;
    const loading = (status || {}).loading;

    if (!loading && fetchingPage <= prevPage) {
      status.loading = true;
      history.replace({
        search: stringify({ ...queryParams,
          page: number(prevPage) + 1
        })
      });
    }
  } : null;
  useEffect(() => {
    const updateState = currentState => {
      setVisibleItems(prevState => {
        if (prevState[0] !== currentState[0] || prevState[1] !== currentState[1] || prevState[2] !== currentState[2]) {
          return currentState;
        }

        return prevState;
      });
    };

    if (!listElem.current || !(childList || []).length) {
      updateState([0, 1, 100, 0]);
      return () => {};
    }

    const handleListScroll = () => {
      if (childList && childList.length) {
        checkVisibleItems(listElem.current, childList.length, updateState);

        if (loadMore && checkEnding(listElem.current)) {
          loadMore();
        }
      }
    };

    const handleResize = () => {
      if (childList && childList.length) {
        checkVisibleItems(listElem.current, childList.length, updateState);

        if (loadMore && checkEnding(listElem.current)) {
          loadMore();
        }
      }
    };

    const handleScroll = () => {
      const isStuck = checkIfStuck(footerElem.current, 'bottom');
      setFooterStuck(isStuck);
    };

    checkVisibleItems(listElem.current, (childList || []).length, updateState);
    const w = listElem.current.ownerDocument.defaultView || listElem.current.ownerDocument.parentView || window;
    const parentScroll = getScrollParent(listElem.current);
    w.addEventListener('resize', handleResize);
    w.addEventListener('scroll', handleScroll);
    w.addEventListener('resize', handleScroll);
    const isStuck = footerElem && footerElem.current && checkIfStuck(footerElem.current, 'bottom');
    setFooterStuck(prevState => {
      if (prevState !== isStuck) {
        return isStuck;
      }

      return prevState;
    });

    if (parentScroll) {
      parentScroll.addEventListener('scroll', handleListScroll);
    } else {
      w.addEventListener('scroll', handleListScroll);
    }

    return () => {
      w.removeEventListener('scroll', handleListScroll);
      w.removeEventListener('resize', handleResize);
      w.removeEventListener('scroll', handleScroll);
      w.removeEventListener('resize', handleScroll);
      parentScroll && parentScroll.removeEventListener('scroll', handleListScroll);
    };
  }, [childList, listElem, loadMore, numberOfVisible]);
  useEffect(() => {
    if (loadMore && listElem.current && listElem.current.children && listElem.current.children.length) {
      const w = listElem.current.ownerDocument.defaultView || listElem.current.ownerDocument.parentView || window;
      const {
        innerHeight
      } = w;
      const rect = listElem.current.getBoundingClientRect();

      if (Math.max(0, rect.top) + rect.height < innerHeight) {
        const childStyle = w.getComputedStyle(listElem.current.children[0]);
        const itemsHeight = parseFloat(childStyle.height) + parseFloat(childStyle.marginBottom);
        const totalItemsSize = (currentPage + 1) * currentPageSize * itemsHeight;

        if (totalItemsSize < Math.max(0, rect.top) + rect.height) {
          loadMore();
        }
      }
    }
  }, [listElem, currentPage, currentPageSize, containerHeight, loadMore]);
  const Card = getCard(card, cardKey);
  const Header = getHeader(headerComponent, {
    user
  });
  const HeaderListComponent = getHeaderMap(headerListComponent, {
    styles,
    params,
    i18n,
    RegularButton,
    checkbox,
    pushModal,
    checkList,
    reload,
    user,
    setIsAllSelected,
    setCheckList,
    childList
  });

  if (!Card) {
    console.warn('No card defined');
    return null;
  }

  const list = ((checkbox && checkList ? checkList : childList) || []).slice(first, first + numberOfVisible).map((child, key) => {
    const path = createLink(basePath, proxy, child, { ...params
    });
    const even = (first + key) % 2 === 0;
    const groupingLine = child.groupingLine ? child.groupingLine : false;
    return /*#__PURE__*/React.createElement("div", {
      key: (key + first) % numberOfVisible,
      className: `cardholder ${styles["cardholder"]} ${even ? styles["even"] || "" : styles["odd"] || ""} ${path || onClick ? styles["has-link"] : ""} ${groupingLine ? styles["grouping-line"] : ""}`,
      onClick: e => {
        onClick && onClick({
          originalEvent: e,
          target: {
            value: child
          }
        });
      }
    }, path ? /*#__PURE__*/React.createElement(NavLink, {
      to: path,
      activeClassName: styles["active-link"],
      draggable: "false",
      onClick: e => {
        let selection = window.getSelection().toString();

        if (selection.length === 0) {
          onClick && onClick({
            originalEvent: e,
            target: {
              value: child
            }
          });
        } else {
          e.preventDefault();
        }
      }
    }, /*#__PURE__*/React.createElement(Card, {
      className: styles["list-item"],
      data: child,
      filter: filtersObj,
      checkbox: checkbox,
      handleChangeCheckbox: onCheckBoxChange
    })) : /*#__PURE__*/React.createElement(Card, {
      className: styles["list-item"],
      data: child,
      filter: filtersObj,
      checkbox: checkbox,
      handleChangeCheckbox: onCheckBoxChange
    }));
  });
  const listStyle = { ...(style || {}),
    height: `${containerHeight}px`,
    paddingTop: `${paddingTop}px`,
    boxSizing: 'border-box'
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, Header, list && list.length ? /*#__PURE__*/React.createElement("div", {
    id: id,
    className: `${styles['root']} ${className || ''} ${modifiersClasses}`
  }, HeaderListComponent, /*#__PURE__*/React.createElement("div", {
    className: styles[wrapper]
  }, header && header.length ? /*#__PURE__*/React.createElement(ListHeader, {
    modifiers: modifiers,
    header: header,
    sort: sort,
    checkbox: checkbox,
    onCheckBoxChange: onCheckBoxChange,
    isAllSelected: isAllSelected
  }) : null, /*#__PURE__*/React.createElement("div", {
    ref: listElem,
    className: styles['list'],
    style: listStyle
  }, list), footer && /*#__PURE__*/React.createElement(ListFooter, {
    listElem: listElem,
    modifiers: modifiers,
    header: header,
    footerStuck: footerStuck,
    footer: footer,
    totalizers: totalizers
  }))) : /*#__PURE__*/React.createElement("div", {
    className: `${styles['root']} ${modifiersClasses}`
  }, HeaderListComponent, /*#__PURE__*/React.createElement("div", {
    ref: listElem,
    className: styles['list'],
    style: listStyle
  }, handleNoResults && childList !== undefined && !activity && /*#__PURE__*/React.createElement("div", {
    className: `placeholder ${styles['empty-content']}`
  }, /*#__PURE__*/React.createElement("small", null, i18n`There are no results!`)))));
};

export default List;